// src/pages/ReferenceDataPage.tsx
//
// ★ PHASE 10 (Task 35) — Annex A 40-47's own configuration, on a screen for
// the first time. `GET /api/reference` (the catalogue) and
// `GET /api/reference/:parameter` (entries as at a service date) have
// existed since the reference-data branch with no UI over either.
//
// ★★ WAVE 3 — WRITABLE, and this paragraph used to say the opposite. It read:
// "Read-only on this screen. POST /api/reference/:parameter/supersede exists
// and is PSD-only, but changing a value that re-prices bills on a service-date
// window is a bigger, separate build (agency scoping, three different value
// shapes — CENTS/WHOLE_DAYS/CODE — each with their own validation) than this
// pass had room for." Every one of those three costs is now paid, below, and
// the note is kept because it is what the wave was scoped against.
//
// ⚠ **THIS IS THE SCREEN THAT MOVES MONEY, and it does so silently if it is
// wrong.** A superseded pegged ward rate re-prices every bill whose DATE OF
// MEDICAL SERVICE falls in the window it opens — not bills submitted after
// today. So the form states the window it is opening in words before it is
// submitted, confirms it, and reports which of the two events happened
// (SUPERSEDED, or RETIRED with no successor).
//
// ⚠ **The client validates NOTHING the server does not.** The bounds shown
// beside each field are the catalogue's own `validation` block — P2C11
// §14.6(c)/(d), published by `GET /api/reference` — so there is one set of
// numbers, enforced at the route and again by the database's own trigger, and
// displayed here. A hand-typed pattern on this screen would be a third copy,
// and the one that drifts.
//
// ⚠ `serviceDate` is REQUIRED and never defaulted to today, for the exact
// reason `EntitlementPage.tsx` gives: every one of these parameters was some
// value on the date of medical service and may be a different value now.
import { plainCode, plainProse } from '../lib/plain-words'
import { CategoryBadge, CodeBadge } from '../components/StatusBadge'
import { useId, useMemo, useState, type FormEvent } from 'react'
import { SgdsButton, SgdsInput, SgdsSelect, SgdsSelectOption, SgdsTextarea } from '@govtechsg/sgds-web-component/react'
import { mayAdministerReferenceData } from '@prs2/auth'
import { api, ApiError } from '../lib/api'
import { useApi } from '../lib/useApi'
import { useAuth } from '../state/AuthContext'
import { ServiceDatePicker } from '../components/ServiceDatePicker'
import { DatePicker } from '../components/DatePicker'
import { AwaitingCard, BuiltCard, EmptyCard, ErrorCard, LoadingCard } from '../components/StateCards'
import type { IndicatorReconciliationResponse } from '../lib/types'
import { parseCents } from '../components/FieldError'
import { targetValue } from '../lib/sgds-dom'
import { formatCents, formatIsoDate } from '../lib/format'
import { useClientSort, type ClientSortColumns } from '../lib/useClientSort'
import { SortableTh, SortAnnouncement } from '../components/SortableTh'
import type { IndicatorVerdict, ReferenceValueEntry } from '../lib/types'
import type { ReferenceEntriesResponse, ReferenceParameterCatalogueEntry } from '../lib/types'
import { patternWords } from '../lib/published-bounds'

/*
  ★★★ BOTH TABLES ON THIS SCREEN SORT IN THE BROWSER, and the condition that makes
  that honest holds for both.

  `GET /api/reference/:parameter` answers EVERY entry in force for one parameter at
  one service date — no `?limit=`, no `?cursor=`, no `?offset=`, and this page has
  no pager. The reconciliation card's verdict set is one row per indicator over a
  fixed, closed indicator list. So the browser holds every row there is in each
  case, and a client sort orders the list rather than a window into it.

  ⚠ That is the ONLY circumstance under which `useClientSort` may be used — see its
  own header, and `sortable-header-census.test.ts`, which fails any page that sorts
  in the browser while also paging.
*/
type VerdictSortKey = 'indicator' | 'register' | 'grammar' | 'agreement'

const VERDICT_COLUMN_LABELS: Readonly<Record<VerdictSortKey, string>> = {
  indicator: 'Indicator',
  register: 'Register says',
  grammar: 'Grammar says',
  agreement: 'Verdict',
}

/*
  ⚠ **Each accessor reproduces what the CELL renders, not one underlying field**,
  and on this table those are genuinely different things. The register column shows
  `billType` on an AGREES, `registerBillType` on a DISAGREES and the words "no entry
  in force" on a SILENT — three fields behind one column. Sorting on any one of them
  would order the rows by a value two thirds of them do not display, which is the
  worst kind of wrong sort: every row stays legible and the order means nothing.
*/
function registerSaysOf(verdict: IndicatorVerdict): string | null {
  if (verdict.agreement === 'AGREES') return verdict.billType ?? null
  if (verdict.agreement === 'DISAGREES') return verdict.registerBillType ?? null
  // ⚠ SILENT sorts into the NULL PARTITION rather than under the letter "n" of
  // "no entry in force". That cell's text is a sentence about absence, not a value,
  // and the rule is that absence sorts last in both directions.
  return null
}

const VERDICT_SORT_COLUMNS: ClientSortColumns<IndicatorVerdict, VerdictSortKey> = {
  indicator: { type: 'text', valueOf: (v) => v.indicator },
  register: { type: 'text', valueOf: registerSaysOf },
  grammar: { type: 'text', valueOf: (v) => v.billType ?? v.declaredBillType ?? null },
  agreement: { type: 'text', valueOf: (v) => v.agreement },
}

type EntrySortKey = 'key' | 'agencyCode' | 'value' | 'effectiveFrom' | 'effectiveUntil' | 'note'

const ENTRY_COLUMN_LABELS: Readonly<Record<EntrySortKey, string>> = {
  key: 'Key',
  agencyCode: 'Agency',
  value: 'Value',
  effectiveFrom: 'Effective from',
  effectiveUntil: 'Effective until',
  note: 'Note',
}

const ENTRY_SORT_COLUMNS: ClientSortColumns<ReferenceValueEntry, EntrySortKey> = {
  key: { type: 'text', valueOf: (e) => e.key },
  /*
    ⚠ A null `agencyCode` means COMMON — "this value applies to every agency" —
    which is a real value rather than an absence, so it must NOT fall into the null
    partition. It sorts by the words the cell actually shows.
  */
  agencyCode: { type: 'text', valueOf: (e) => e.agencyCode ?? 'Common (all agencies)' },
  /*
    ★ NUMERIC, and this is the accessor worth reading twice. Every entry of one
    parameter carries the same `kind`, so this column is all money, all days or all
    codes and never a mixture. Sorting the RENDERED STRING instead would put nine
    dollars above ten dollars — the lexical answer, and the wrong one on a money
    column, in the one place in this product where that mistake would be about
    public money.
  */
  value: {
    type: 'number',
    valueOf: (e) => {
      if (e.value === null) return null
      if (e.value.kind === 'CENTS') return e.value.cents
      if (e.value.kind === 'WHOLE_DAYS') return e.value.days
      // A CODE has no numeric order. Null puts every CODE row in the null
      // partition together rather than inventing an order for them; since a
      // parameter never mixes kinds, that is the whole column and its own order is
      // the server's.
      return null
    },
  },
  effectiveFrom: { type: 'date', valueOf: (e) => e.effective.from },
  /*
    ⚠ An open-ended window (`until === null`) renders as "Open-ended" and sorts LAST
    in both directions — which here is right by rule AND right by meaning: an open
    window ends later than every closed one.
  */
  effectiveUntil: { type: 'date', valueOf: (e) => e.effective.until },
  note: { type: 'text', valueOf: (e) => e.note },
}

function formatReferenceValue(value: ReferenceEntriesResponse['entries'][number]['value']): string {
  if (value === null) return 'None'
  switch (value.kind) {
    case 'CENTS':
      return formatCents(value.cents)
    case 'WHOLE_DAYS':
      return `${value.days} day${value.days === 1 ? '' : 's'}`
    case 'CODE':
      return value.code
    default:
      return 'Unknown'
  }
}

/**
 * ★★ WAVE 9 — the parameter's status card, chosen by what the SERVER published.
 *
 * ## The defect this closes
 *
 * This screen used to render every `referenceModelNote` inside `UnbuiltCard`,
 * whose closing line is a fixed one naming a precondition — formerly *"Not built yet. No action will make data appear
 * here."* — and the note it printed above that line said, for six of the ten
 * parameters, *"Read at POST /api/bills/:reference/compute"*. After the Wave 8
 * episode-control pass it was eight of ten, and the eight are the ones the money
 * rides: the pegged ward rate, the service-injury ward minimum, the child
 * subsidised-length cap, the community-hospital eligibility flag. So the card
 * contradicted its own body text, on this product's most load-bearing
 * configuration screen, and told a PSD user that a control which had just capped
 * a real bill was not built.
 *
 * ## Why the status is not derived here
 *
 * Matching the note's WORDS — sniffing for `/compute`, or for "Not read" — would
 * put a copy of the rule in the layer furthest from the thing it describes, and a
 * note reworded by whoever next edits the migration would silently flip the card
 * back. `058_reference_model_status.sql` publishes
 * `reference_model_status` instead, refused at the database to one of three
 * values, and this function is a total switch over that union: a fourth value
 * cannot reach a default branch, because there is no default branch.
 *
 * `services/administration/src/reference-model-status.test.ts` is what keeps the
 * published status honest — it counts resolution sites in the source per
 * parameter and fails the build when a status and the code disagree.
 */
function ReferenceModelStatusCard({ row }: { row: ReferenceParameterCatalogueEntry }) {
  const note = row.referenceModelNote ?? ''
  switch (row.referenceModelStatus) {
    case 'READ':
      return <BuiltCard title={`${row.label}: read when a bill is computed`} note={note} />
    case 'PARTIALLY_READ':
      return (
        <BuiltCard
          title={`${row.label}: partly read`}
          note={note}
          caveat="One axis of this series is read when a bill is computed and another is not. The note above names which."
        />
      )
    case 'NOT_READ':
      // ⚠ The CONDITION is composed here while the note and the STATUS come from
      // the server, and the split is deliberate: `reference_model_status` is a
      // fact about what the code does today (S-011 — the screen renders what the
      // server published and derives no status of its own), whereas "what would
      // have to happen" is a statement about the roadmap, which no column holds.
      // It is phrased so it stays true for any parameter that reaches this branch,
      // not just the one that is here today.
      return (
        <AwaitingCard
          title={`${row.label}: held, and ready for its first reader`}
          note={note}
          availableWhen="a step that consults it is built: pro-ration does not read it today"
        />
      )
  }
}

/**
 * ★★★ WAVE 12 (P5) — does the adjustment-indicator register agree with the
 * published file-exchange grammar?
 *
 * ══ WHY THIS CARD EXISTS ══
 *
 * From Wave 12 a DISAGREEMENT refuses every HSP record carrying that indicator.
 * That is the right answer and it is a cliff: a PSD user supersedes an entry on
 * this very screen on Tuesday, and Wednesday's delivery is refused record by
 * record with nothing having warned them. This is the warning — the same state,
 * before a file arrives rather than after 4,879 refusals.
 *
 * ⚠ **Three states, never a tick.** `AGREES` / `SILENT` / `DISAGREES` have
 * different remedies: nobody authored the entry, versus somebody authored a
 * different one. A single tick-or-cross would send half its readers to the wrong
 * action — the same reasoning `availability()` follows on
 * `IdentityProvidersPage`.
 *
 * ⚠ **`reconciled` and `consequence` are RENDERED, not decided here.** The route
 * computes the verdict with the same function the ingest path calls, so this
 * screen cannot hold a second opinion about whether a delivery will be refused.
 * A screen that worked out the consequence for itself would be a second copy of
 * the ingest rule, which is the defect this whole feature exists to end.
 */
function IndicatorReconciliationCard({ data }: { data: IndicatorReconciliationResponse }) {
  const verdictSort = useClientSort(data.verdicts, VERDICT_SORT_COLUMNS)
  return (
    <div
      className={`pt-card border-l-4 ${
        data.reconciled
          ? 'border-l-[var(--sgds-success-color-default)]'
          : 'border-l-[var(--sgds-danger-color-default)]'
      }`}
    >
      <h3 className="font-semibold mb-1">
        {data.reconciled
          ? 'Register and published grammar agree'
          : 'Register and published grammar DISAGREE'}
      </h3>
      <p className="text-sm text-[var(--sgds-body-color-subtle)] mb-3 max-w-[80ch]">
        As at the date of medical service {data.resolvedServiceDate}. {plainProse(data.consequence)}
      </p>
      {/*
        ⚠ `busy={false}`, and that is correct rather than lazy. `sortAnnouncement`
        takes `busy` because a SERVER sort leaves the old order on screen between the
        click and the response, and announcing then would state a change that has not
        happened yet. This sort is synchronous: by the time this renders, the rows ARE
        in the new order, so there is no in-flight window to stay silent through.
      */}
      <SortAnnouncement
        sort={verdictSort.sort}
        interacted={verdictSort.interacted}
        busy={false}
        columnLabels={VERDICT_COLUMN_LABELS}
        rowCount={verdictSort.rows.length}
      />
      {/* ★ WAVE 26 — the house table and its print-safe wrapper, like every other list. */}
      <div className="pt-table-wrap">
      <table className="pt-table">
        <caption className="sr-only">
          Bill-type indicators as at {data.resolvedServiceDate}: what the register holds, what the published grammar holds, and whether they agree
        </caption>
        <thead>
          <tr className="text-left border-b border-[var(--sgds-border-color-muted)]">
            <SortableTh label={VERDICT_COLUMN_LABELS.indicator} sortKey="indicator" sort={verdictSort.sort} onSort={verdictSort.onSort} />
            <SortableTh label={VERDICT_COLUMN_LABELS.register} sortKey="register" sort={verdictSort.sort} onSort={verdictSort.onSort} />
            <SortableTh label={VERDICT_COLUMN_LABELS.grammar} sortKey="grammar" sort={verdictSort.sort} onSort={verdictSort.onSort} />
            <SortableTh label={VERDICT_COLUMN_LABELS.agreement} sortKey="agreement" sort={verdictSort.sort} onSort={verdictSort.onSort} />
          </tr>
        </thead>
        <tbody>
          {verdictSort.rows.map((verdict) => (
            <tr key={verdict.indicator} className="border-b border-[var(--sgds-border-color-muted)] last:border-0">
              <td className="py-1 pr-4 font-mono">{verdict.indicator}</td>
              <td className="py-1 pr-4">
                {/*
                  ⚠ A SILENT register has no value to show, and an em dash alone
                  would read as "blank" rather than as "nobody has authored this".
                  Absence has a meaning here and the cell states it.
                */}
                {verdict.agreement === 'AGREES'
                  ? plainCode(verdict.billType ?? '')
                  : verdict.agreement === 'DISAGREES'
                    ? plainCode(verdict.registerBillType ?? '')
                    : 'no entry in force'}
              </td>
              <td className="py-1 pr-4">{plainCode(verdict.billType ?? verdict.declaredBillType ?? '')}</td>
              <td className="py-1">
                {verdict.agreement === 'AGREES' ? (
                  <CategoryBadge kind="state" value="AGREES" label="Agrees" />
                ) : verdict.agreement === 'DISAGREES' ? (
                  <>
                    <CategoryBadge kind="state" value="DISAGREES" label="Disagrees" /> records carrying it are refused
                  </>
                ) : (
                  <>
                    <CategoryBadge kind="state" value="NO_ENTRY" label="No entry in force" /> records carrying it are refused
                  </>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      </div>
    </div>
  )
}

/**
 * What the officer confirmed — frozen at the moment they pressed Review.
 *
 * ⚠ **THE CONFIRMATION MUST NOT TRACK THE FORM.** The first draft rendered the
 * sentence from live state and re-read live state when the officer confirmed,
 * which is wrong twice over on a screen that re-prices bills:
 *
 *   * an edit made while the dialog is open silently changes what will be sent,
 *     so the officer confirms one change and applies another; and
 *   * clearing a required field mid-confirmation made "Yes, apply it" return
 *     early and do NOTHING, with no message — the same silent-failure shape
 *     `SupportTicketsPage.handleTransition` carried.
 *
 * Freezing the payload closes both: what is described is what is sent, and it
 * cannot become unsendable between the two clicks.
 */
interface PendingWrite {
  readonly parameter: string
  readonly key: string
  readonly agencyCode: string | null
  readonly effectiveFrom: string
  readonly note: string
  readonly retire: boolean
  /** `null` retires; otherwise the exact typed value, already parsed. */
  readonly value: { kind: 'CENTS'; cents: number } | { kind: 'WHOLE_DAYS'; days: number } | { kind: 'CODE'; code: string } | null
  /** The sentence the officer was shown, kept so the two can never disagree. */
  readonly sentence: string
}

type WriteState =
  | { status: 'idle' }
  | { status: 'confirming'; pending: PendingWrite }
  | { status: 'submitting' }
  | { status: 'error'; message: string }
  | { status: 'success'; message: string }

/**
 * A typed value from what the officer typed, or the reason it is not one.
 *
 * ⚠ **The KIND is taken from the CATALOGUE, never from the form.** That is the
 * same decision `routes/reference.ts`'s own `parseValue` makes, and for the same
 * reason: a caller who could choose the kind could put a code in a parameter
 * that takes days, and the database would refuse it correctly but with a driver
 * error rather than a message naming the field.
 *
 * ⚠ **Money is parsed to whole CENTS, and a value with more than two decimal
 * places is REFUSED rather than rounded.** Rounding somebody's ward rate down a
 * cent and saying "saved" is the quiet wrong answer this repository's own
 * agreements name first — and it would re-price every bill in the window.
 */
export function parseReferenceValue(
  kind: ReferenceParameterCatalogueEntry['valueKind'],
  raw: string,
): { value: { kind: 'CENTS'; cents: number } | { kind: 'WHOLE_DAYS'; days: number } | { kind: 'CODE'; code: string } } | { error: string } {
  const trimmed = raw.trim()
  if (trimmed.length === 0) return { error: 'Enter a value, or tick “Retire this entry” to close the window with no successor.' }

  switch (kind) {
    case 'CENTS': {
      // ⚠ ONE implementation of dollars-and-cents, in `FieldError`, now that the
      // estimate form needs the same rule. An S-011 finding is exactly this shape
      // — a second copy of a rule — and money is the worst place to keep one.
      //
      // ⚠ The two MESSAGES stay here, because they are not the same sentence on
      // the two screens: on THIS one a rounded rate RE-PRICES EVERY BILL in the
      // window it opens, which is a sharper consequence than a mis-quoted
      // estimate and worth saying in the officer's own context. A shape-valid
      // input can fail for exactly one reason, so the branch below is decidable
      // rather than a guess about which message applies.
      const parsed = parseCents(trimmed)
      if ('error' in parsed) {
        return /^\d+(\.\d{1,2})?$/.test(trimmed)
          ? { error: 'That amount is too large to store exactly.' }
          : {
              error:
                'Enter an amount in dollars and cents, for example 128.50. More than two decimal places is refused ' +
                'rather than rounded: a rate rounded silently re-prices every bill in the window it opens.',
            }
      }
      return { value: { kind: 'CENTS', cents: parsed.cents } }
    }
    case 'WHOLE_DAYS': {
      if (!/^\d+$/.test(trimmed)) return { error: 'Enter a whole number of days, for example 30.' }
      const days = Number(trimmed)
      if (!Number.isSafeInteger(days)) return { error: 'That number of days is out of range.' }
      return { value: { kind: 'WHOLE_DAYS', days } }
    }
    case 'CODE':
    default:
      return { value: { kind: 'CODE', code: trimmed } }
  }
}

/** What the officer is about to do, in one sentence, before they do it. */
export function describeChange(args: {
  readonly label: string
  readonly key: string
  readonly agencyCode: string | null
  readonly effectiveFrom: string
  readonly retire: boolean
  readonly valueText: string
}): string {
  const scope = args.agencyCode === null ? 'every agency' : `agency ${args.agencyCode}`
  const when = `from the date of medical service ${args.effectiveFrom}`
  return args.retire
    ? `Retire ${args.label} for ${args.key} (${scope}) ${when}. From that date the parameter has NO value at all: ` +
        'any bill priced against it will be refused rather than defaulted.'
    : `Set ${args.label} for ${args.key} (${scope}) to ${args.valueText} ${when}. Every bill whose service date ` +
        'falls in that window prices against the new value, including bills submitted before today.'
}

export function ReferenceDataPage() {
  const dateId = useId()
  const { user } = useAuth()
  const catalogue = useApi(() => api.referenceParameters(), [])
  const [selected, setSelected] = useState('')
  const [serviceDate, setServiceDate] = useState('')
  // ★★★ WAVE 12 (P4) — the bi-temporal axis, beside the service-date one.
  // `serviceDate` asks WHICH RULE APPLIES; `asRecordedAt` asks WHAT THE SYSTEM
  // KNEW. An auditor checking a disputed bill needs both, and this screen could
  // only ever put the first.
  const [asRecordedAt, setAsRecordedAt] = useState('')
  const [submitted, setSubmitted] = useState<{ parameter: string; serviceDate: string; asRecordedAt: string } | null>(null)

  // ★★ WAVE 3 — the write half.
  const [writeKey, setWriteKey] = useState('')
  const [writeAgency, setWriteAgency] = useState('')
  const [writeValue, setWriteValue] = useState('')
  const [writeEffectiveFrom, setWriteEffectiveFrom] = useState('')
  const [writeNote, setWriteNote] = useState('')
  const [retire, setRetire] = useState(false)
  const [write, setWrite] = useState<WriteState>({ status: 'idle' })

  const entries = useApi(
    () =>
      submitted === null
        ? Promise.resolve(null)
        : api.referenceEntries(submitted.parameter, submitted.serviceDate, submitted.asRecordedAt),
    [submitted?.parameter, submitted?.serviceDate, submitted?.asRecordedAt],
  )

  /**
   * ★★★ WAVE 12 (P5) — fetched only for the one parameter it is meaningful for.
   *
   * ⚠ Keyed on BOTH `selected` and `serviceDate`, in the dependency array as
   * well as in the closure. Wave 12 already shipped a filter that took effect
   * only on an unrelated reload because the query read a value the dependency
   * array did not name; the register is effective-dated, so the same omission
   * here would show a reader last month’s verdict under this month’s date.
   */
  /*
    ⚠ `useMemo` so the array identity is stable across renders. Without it every
    render hands `useClientSort` a fresh array, its own `useMemo` sees a changed
    dependency and re-sorts on every keystroke in the form above.
  */
  const entryRows = useMemo(() => entries.data?.entries ?? [], [entries.data])
  const entrySort = useClientSort(entryRows, ENTRY_SORT_COLUMNS)

  const reconciliation = useApi(
    () =>
      selected === 'ADJUSTMENT_INDICATOR' && serviceDate.length > 0
        ? api.indicatorReconciliation(serviceDate)
        : Promise.resolve(null),
    [selected, serviceDate],
  )

  const canSubmit = selected.length > 0 && serviceDate.length > 0

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault()
    if (!canSubmit) return
    setSubmitted({ parameter: selected, serviceDate, asRecordedAt })
  }

  const parameters = catalogue.data?.parameters ?? []
  const selectedRow = parameters.find((p) => p.parameter === selected)

  /**
   * ⚠ **The SAME predicate `routes/reference.ts` calls, imported from
   * `@prs2/auth` — never a role string compared on this screen.**
   *
   * `mayAdministerReferenceData` is one function; the route runs it to answer
   * 403 and this runs it to decide whether to render the form at all. Restating
   * the rule here as `role === 'PSD_USER'` would be a second statement of an
   * authorisation rule, and the two would drift the first time the rule changed
   * — which is the defect the 2026-08-18 navigation gate exists to prevent, one
   * screen further in.
   *
   * ⚠ Hiding the form is PRESENTATION. The route refuses independently, and
   * `reference-write.test.ts` proves it against a non-PSD session.
   */
  const mayWrite = user !== null && mayAdministerReferenceData(user.scope)

  const parsedValue = selectedRow === undefined || retire ? null : parseReferenceValue(selectedRow.valueKind, writeValue)
  /**
   * ⚠ **An UNTOUCHED field is not an invalid one**, and this used to announce
   * that it was. `role="alert"` fires the moment its content appears, so
   * rendering the parser's "enter a value" message for an empty field meant
   * every officer who opened the form was told, by a screen reader,
   * interrupting whatever else was speaking, that they had made a mistake
   * before touching anything. Found by reading the DOM test's own output.
   *
   * The field is still REQUIRED — `canWrite` below is false while it is empty,
   * so the button does not arm. What changes is only whether the product
   * accuses the officer of an error they have not made yet.
   */
  const valueProblem =
    writeValue.trim().length > 0 && parsedValue !== null && 'error' in parsedValue ? parsedValue.error : null
  const canWrite =
    selectedRow !== undefined &&
    writeKey.trim().length > 0 &&
    writeEffectiveFrom.length > 0 &&
    // ⚠ Reads the PARSER, not `valueProblem` — the latter is suppressed while
    // the field is untouched (see its own note), and a button armed on an empty
    // required field is exactly the defect that suppression could have caused.
    (retire || (parsedValue !== null && 'value' in parsedValue))

  const changeSentence =
    selectedRow === undefined
      ? ''
      : describeChange({
          label: selectedRow.valueLabel,
          key: writeKey.trim(),
          agencyCode: writeAgency.trim().length > 0 ? writeAgency.trim() : null,
          effectiveFrom: writeEffectiveFrom,
          retire,
          valueText: writeValue.trim(),
        })

  /**
   * Send exactly what was confirmed.
   *
   * ⚠ Takes the frozen {@link PendingWrite} as an argument rather than reading
   * the form — see that type's own note. Nothing in this function consults
   * `writeKey`, `writeValue` or `retire`, deliberately.
   */
  const handleWrite = async (pending: PendingWrite) => {
    setWrite({ status: 'submitting' })
    try {
      const outcome = await api.supersedeReferenceValue(pending.parameter, {
        key: pending.key,
        effectiveFrom: pending.effectiveFrom,
        ...(pending.agencyCode !== null && { agencyCode: pending.agencyCode }),
        value: pending.value,
        ...(pending.note.length > 0 && { note: pending.note }),
      })
      setWrite({
        status: 'success',
        message:
          outcome.outcome === 'RETIRED'
            ? `Retired. ${plainCode(pending.parameter)} for ${pending.key} has no value from ${outcome.effectiveUntil}: a bill priced against it on or after that date is refused, never defaulted.`
            : `Superseded. A new window opens on ${outcome.entry.effective.from}${outcome.closedPrevious ? ', and the window it replaces is now closed' : '; nothing was open before it, so nothing was closed'}.`,
      })
      setWriteValue('')
      setWriteNote('')
      // ⚠ The table below reads as at the SERVICE DATE the officer asked about,
      // which may be outside the window just opened — so it is reloaded rather
      // than patched, and it may legitimately show no change. Saying that is
      // better than making the row appear and implying the two dates are one.
      if (submitted !== null) entries.reload()
    } catch (error) {
      setWrite({
        status: 'error',
        message: error instanceof ApiError ? error.message : 'Could not save this change.',
      })
    }
  }

  return (
    <div className="flex flex-col gap-6">
      <div>
        <h1 className="text-3xl mb-2">Reference data</h1>
        <p className="text-[var(--sgds-body-color-subtle)] max-w-[70ch]">
          Pro-ration configuration: pegged ward rates, grace periods, MBIM validity windows and the rest of the
          settings that drive pro-ration. Every value is effective-dated on the <strong>date of medical
          service</strong>, never today&rsquo;s date, and never edited in place: a change opens a new window and
          closes the old one.{' '}
          {mayWrite
            ? 'Changing a value here re-prices every bill whose service date falls in the window you open, including bills submitted before today.'
            : 'Read-only for your active identity: only a PSD officer may change these values.'}
        </p>
      </div>

      {catalogue.error !== null ? (
        <ErrorCard title="Could not load the parameter catalogue" message={catalogue.error} onRetry={catalogue.reload} />
      ) : catalogue.initialLoading ? (
        <LoadingCard label="Loading parameter catalogue" rows={3} />
      ) : parameters.length === 0 ? (
        <EmptyCard title="No parameters readable" message="Your active identity's role holds no readable pro-ration configuration parameter." />
      ) : (
        <form className="pt-card flex flex-col gap-5" onSubmit={handleSubmit}>
          <SgdsSelect label="Parameter" value={selected} onSgdsChange={(e) => setSelected((e.target as HTMLSelectElement).value)}>
            <SgdsSelectOption value="">Choose a parameter&hellip;</SgdsSelectOption>
            {parameters.map((p) => (
              <SgdsSelectOption key={p.parameter} value={p.parameter}>
                {p.label}
              </SgdsSelectOption>
            ))}
          </SgdsSelect>

          {selectedRow !== undefined && selectedRow.referenceModelNote !== null && (
            <ReferenceModelStatusCard row={selectedRow} />
          )}

          {/*
            ★★★ WAVE 12 (P5) — placed under the status card and ABOVE the service-date
            picker, because it is a fact about the parameter rather than a result of a
            query the officer has yet to submit. It appears as soon as a date is typed:
            waiting for Submit would hide a disagreement behind an action a reader who
            only came to check the configuration would never take.
          */}
          {/*
            ★★ WAVE 24 — a failed reconciliation READ used to render nothing.

            `catalogue.error` and `entries.error` are both rendered on this page;
            `reconciliation.error` was never read anywhere in the file, so a
            failure left the card simply absent. An officer cannot tell an
            indicator set that reconciles cleanly from one the screen could not
            fetch — and on this card the two mean opposite things.
          */}
          {reconciliation.error !== null && (
            <ErrorCard
              title="Could not load the indicator reconciliation"
              message={reconciliation.error}
              onRetry={reconciliation.reload}
            />
          )}
          {reconciliation.data !== null && reconciliation.data !== undefined && (
            <IndicatorReconciliationCard data={reconciliation.data} />
          )}

          <div>
            <span id={dateId} className="block text-sm font-semibold mb-1">
              Service date <span aria-hidden="true">*</span>
              <span className="sr-only">required</span>
            </span>
            <ServiceDatePicker value={serviceDate} onChange={setServiceDate} labelledBy={dateId} />
          </div>

          {/*
            ★★★ WAVE 12 (P4) — THE SECOND TIME AXIS, on the catalogue where it decides
            money. `GET /api/reference/:parameter` has accepted `?asRecordedAt=` since
            Task 15 and no screen ever sent it, so *"what did the system hold when this
            bill was computed?"* — the question an auditor asks about a disputed bill —
            could not be put here at all.

            ⚠ The two axes answer DIFFERENT questions and the label has to say so, or a
            reader will treat this as a second service date: `serviceDate` decides WHICH
            RULE APPLIES to care given then; this decides WHAT THE SYSTEM KNEW at that
            instant. A bill computed in March under a rate superseded in June needs its
            own service date AND a March instant here.
          */}
          <div>
            <span id={`${dateId}-recorded`} className="block text-sm font-semibold mb-1">
              As the system knew it at <span className="font-normal text-[var(--sgds-color-subtle)]">(optional)</span>
            </span>
            <input
              type="text"
              className="pt-input pt-mono"
              placeholder="2026-03-31T23:59:59.999Z"
              aria-labelledby={`${dateId}-recorded`}
              aria-describedby={`${dateId}-recorded-help`}
              value={asRecordedAt}
              onChange={(event) => setAsRecordedAt(event.target.value)}
            />
            <span id={`${dateId}-recorded-help`} className="text-xs text-[var(--sgds-body-color-subtle)] block mt-1 max-w-[70ch]">
              A UTC instant. Leave empty for what is held now. This is <strong>not</strong> a second service date: it
              reconstructs what this system would have ANSWERED at that moment, so a value recorded later is dropped
              and a window closed later is handed back open. Use it to check a bill against the rules that were in
              force when it was computed.
            </span>
          </div>

          <div>
            <SgdsButton type="submit" variant="primary" disabled={!canSubmit} loading={entries.loading}>
              Show entries
            </SgdsButton>
          </div>
        </form>
      )}

      {entries.error !== null && <ErrorCard title="Could not load entries" message={entries.error} />}

      {mayWrite && selectedRow !== undefined && (
        <div className="pt-card flex flex-col gap-5 pt-no-print">
          <div>
            <h2 className="text-xl mb-1">Supersede {selectedRow.label}</h2>
            <p className="text-sm text-[var(--sgds-body-color-subtle)] max-w-[70ch]">
              {selectedRow.story}: superseding is the only way a value changes; nothing is ever edited in
              place. The date below is the <strong>date of medical service</strong> the new value takes effect
              from, and it is never defaulted to today.
            </p>
          </div>

          {/*
            ⚠ Every bound shown here comes from the catalogue the SERVER
            published (P2C11 §14.6(c)/(d)) — the same numbers the route
            validates against and the database's trigger enforces. Nothing on
            this screen re-states them as its own literal.
          */}
          <form
            className="grid gap-4 sm:grid-cols-2"
            onSubmit={(event: FormEvent) => {
              event.preventDefault()
              if (!canWrite || selectedRow === undefined) return
              // FROZEN HERE, and read nowhere else — see PendingWrite's note.
              setWrite({
                status: 'confirming',
                pending: {
                  parameter: selectedRow.parameter,
                  key: writeKey.trim(),
                  agencyCode: selectedRow.agencyScoped && writeAgency.trim().length > 0 ? writeAgency.trim() : null,
                  effectiveFrom: writeEffectiveFrom,
                  note: writeNote.trim(),
                  retire,
                  value: retire ? null : parsedValue !== null && 'value' in parsedValue ? parsedValue.value : null,
                  sentence: changeSentence,
                },
              })
            }}
          >
            <label className="flex flex-col gap-1 text-sm">
              <span className="font-semibold">
                {selectedRow.keyLabel} <span aria-hidden="true">*</span>
                <span className="sr-only">required</span>
              </span>
              <input
                type="text"
                className="pt-native-date"
                value={writeKey}
                required
                minLength={selectedRow.validation.keyMinLength}
                maxLength={selectedRow.validation.keyMaxLength}
                pattern={selectedRow.validation.keySyntax}
                aria-describedby={`${dateId}-key-help`}
                onChange={(e) => setWriteKey(e.target.value)}
              />
              <span id={`${dateId}-key-help`} className="text-xs text-[var(--sgds-body-color-subtle)]">
                {/* the pattern in words, never as a regular expression: the same helper every other bounded field uses */}
                {patternWords(selectedRow.validation.keySyntax) ?? 'Use the format the system accepts'}, {selectedRow.validation.keyMinLength} to{' '}
                {selectedRow.validation.keyMaxLength} characters.
              </span>
            </label>

            <label className="flex flex-col gap-1 text-sm">
              <span className="font-semibold">
                Effective from (date of medical service) <span aria-hidden="true">*</span>
                <span className="sr-only">required</span>
              </span>
              <DatePicker value={writeEffectiveFrom} onChange={setWriteEffectiveFrom} required />
            </label>

            {/*
              ⚠ Rendered ONLY where the catalogue says the parameter is
              agency-scoped. Sending an agency for a common parameter is a 400
              from the route, and offering the field would invite it.
            */}
            {selectedRow.agencyScoped && (
              <SgdsInput placeholder=""
                label="Agency code (leave blank for every agency)"
                value={writeAgency}
                onSgdsInput={(e) => setWriteAgency(targetValue(e))}
              />
            )}

            {!retire && (
              <label className="flex flex-col gap-1 text-sm">
                <span className="font-semibold">
                  {selectedRow.valueLabel} <span aria-hidden="true">*</span>
                  <span className="sr-only">required</span>
                </span>
                <input
                  type="text"
                  className="pt-native-date"
                  value={writeValue}
                  required
                  aria-invalid={valueProblem !== null}
                  aria-describedby={`${dateId}-value-help`}
                  onChange={(e) => setWriteValue(e.target.value)}
                />
                <span id={`${dateId}-value-help`} className="text-xs text-[var(--sgds-body-color-subtle)]">
                  {selectedRow.valueKind === 'CENTS'
                    ? 'Dollars and cents, for example 128.50. More than two decimal places is refused rather than rounded.'
                    : selectedRow.valueKind === 'WHOLE_DAYS'
                      ? 'A whole number of days, for example 30.'
                      : `A code${selectedRow.validation.valueSyntax === null ? '' : ` matching ${selectedRow.validation.valueSyntax}`}.`}
                </span>
                {valueProblem !== null && (
                  <span className="text-xs text-[var(--sgds-danger-color-emphasis)]" role="alert">
                    {valueProblem}
                  </span>
                )}
              </label>
            )}

            <label className="flex items-center gap-2 text-sm sm:col-span-2">
              <input
                type="checkbox"
                checked={retire}
                onChange={(e) => {
                  setRetire(e.target.checked)
                  setWrite({ status: 'idle' })
                }}
              />
              Retire this entry instead: close the open window with <strong>no successor</strong>
            </label>

            <div className="sm:col-span-2">
              <SgdsTextarea placeholder="" label="Note (optional)" value={writeNote} onSgdsInput={(e) => setWriteNote(targetValue(e))} />
            </div>

            <div className="sm:col-span-2 flex items-center gap-3">
              <SgdsButton type="submit" variant="primary" disabled={!canWrite}>
                Review this change
              </SgdsButton>
            </div>
          </form>

          {/*
            ★★ THE CONFIRMATION, and it is not decoration. This is the one
            screen in the product where a keystroke re-prices bills that have
            already been submitted — Annex A 2-4's rule is that entitlement is
            evaluated as at the DATE OF MEDICAL SERVICE, so a window opened in
            the past reaches backwards. `DEMO-READINESS-PLAN.md` §Wave 5 records
            confirmation being inconsistent across this product; this surface is
            the one that most needs it.
          */}
          {write.status === 'confirming' && (
            <div className="pt-card border border-[var(--sgds-border-color-muted)]" role="alertdialog" aria-label="Confirm this change">
              {/* The sentence as it was when Review was pressed — never re-derived from a form the officer may since have edited. */}
              <p className="m-0 mb-3">{write.pending.sentence}</p>
              <div className="flex gap-2">
                <SgdsButton type="button" variant="primary" onClick={() => void handleWrite(write.pending)}>
                  Yes, apply it
                </SgdsButton>
                <SgdsButton type="button" variant="outline" onClick={() => setWrite({ status: 'idle' })}>
                  Cancel
                </SgdsButton>
              </div>
            </div>
          )}

          {/*
            ⚠ `role="alert"` on BOTH outcomes — WCAG 2.2 3.3.1. A result
            rendered silently under a form is invisible to a screen reader, and
            on this surface the result is "money moved" or "money did not".
          */}
          {write.status === 'submitting' && (
            <p className="text-sm m-0" role="status">
              Applying&hellip;
            </p>
          )}
          {write.status === 'error' && (
            <p className="text-sm text-[var(--sgds-danger-color-emphasis)] m-0" role="alert">
              {plainProse(write.message)}
            </p>
          )}
          {write.status === 'success' && (
            <p className="text-sm text-[var(--sgds-success-color-emphasis)] m-0" role="alert">
              {plainProse(write.message)}
            </p>
          )}
        </div>
      )}

      {submitted !== null && entries.data !== undefined && entries.data !== null && (
        <div className="pt-card">
          <h2 className="text-lg mb-4">
            {selectedRow?.label ?? submitted.parameter} as at {formatIsoDate(entries.data.resolvedServiceDate)}
          </h2>
          {/*
            ★★★ WAVE 12 (P4) — SAY WHEN THIS IS A RECONSTRUCTION. Nothing else on this
            card would tell a reader: the columns, the values and the dates all look
            exactly as they do for the present, and this is the catalogue whose numbers
            decide money. A superseded rate mistaken for the current one is a wrong
            payment made confidently.

            ⚠ And it states the half nobody expects — a window closed after that
            instant reads as still open — plus the line that matters most here: do not
            compute a bill from this.
          */}
          {submitted.asRecordedAt.length > 0 && (
            <div role="status" className="pt-well p-4 mb-4">
              <p className="font-semibold">
                Reconstructed as the system knew it at <span className="pt-mono">{submitted.asRecordedAt}</span>
              </p>
              <p className="mt-2 text-sm text-[var(--sgds-body-color-subtle)]">
                These are not necessarily the values in force now. Anything recorded after that instant is absent, and
                a window closed after it is shown as still open because at that moment nobody had closed it.{' '}
                <strong>Do not compute or decide a bill from this view</strong>: it is for checking what was applied,
                not for applying.
              </p>
            </div>
          )}
          {entries.data.entries.length === 0 ? (
            <EmptyCard
              title="No entry in force"
              message={
                submitted.asRecordedAt.length > 0
                  ? `Nothing had been recorded for this parameter, for that service date, as at ${submitted.asRecordedAt}. That is an answer about the past, not a missing value: move the instant later, or clear it to see what is held now.`
                  : 'Nothing was in force for this parameter on the chosen service date.'
              }
            />
          ) : (
            <>
            {/*
              ⚠ `busy={false}` — see IndicatorReconciliationCard's own note. This sort
              is synchronous, so there is no in-flight window to stay silent through.
            */}
            <SortAnnouncement
              sort={entrySort.sort}
              interacted={entrySort.interacted}
              busy={false}
              columnLabels={ENTRY_COLUMN_LABELS}
              rowCount={entrySort.rows.length}
            />
            <div className="pt-table-wrap">
              <table className="pt-table">
                <caption className="sr-only">
                  Entries in force for this parameter on the chosen service date
                  {submitted.asRecordedAt.length > 0 ? `, as the system knew them at ${submitted.asRecordedAt}` : ''}
                </caption>
                <thead>
                  <tr>
                    <SortableTh label={ENTRY_COLUMN_LABELS.key} sortKey="key" sort={entrySort.sort} onSort={entrySort.onSort} />
                    <SortableTh label={ENTRY_COLUMN_LABELS.agencyCode} sortKey="agencyCode" sort={entrySort.sort} onSort={entrySort.onSort} />
                    <SortableTh label={ENTRY_COLUMN_LABELS.value} sortKey="value" sort={entrySort.sort} onSort={entrySort.onSort} align="right" />
                    <SortableTh label={ENTRY_COLUMN_LABELS.effectiveFrom} sortKey="effectiveFrom" sort={entrySort.sort} onSort={entrySort.onSort} />
                    <SortableTh label={ENTRY_COLUMN_LABELS.effectiveUntil} sortKey="effectiveUntil" sort={entrySort.sort} onSort={entrySort.onSort} />
                    <SortableTh label={ENTRY_COLUMN_LABELS.note} sortKey="note" sort={entrySort.sort} onSort={entrySort.onSort} />
                  </tr>
                </thead>
                <tbody>
                  {entrySort.rows.map((entry, index) => (
                    <tr key={`${entry.key}-${entry.agencyCode ?? 'common'}-${index}`}>
                      <td title={entry.key}>{plainCode(entry.key)}</td>
                      <td>{entry.agencyCode ?? 'Common (all agencies)'}</td>
                      <td className="pt-tabular pt-num font-semibold">{formatReferenceValue(entry.value)}</td>
                      <td className="pt-tabular">{formatIsoDate(entry.effective.from)}</td>
                      <td className="pt-tabular">{entry.effective.until !== null ? formatIsoDate(entry.effective.until) : 'Open-ended'}</td>
                      <td className="text-sm text-[var(--sgds-body-color-subtle)]">{entry.note ?? 'None'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            </>
          )}
        </div>
      )}
    </div>
  )
}
